"use client";

/**
 * Schedule Detail Modal
 * Full-detail view for a schedule item, shown when clicking an item in the public view.
 * When the schedule item is linked to an activity, exposes the existing sign-up / waitlist flow.
 */

import type React from "react";
import { useCallback, useMemo } from "react";
import { Award, Clock, MapPin, User } from "lucide-react";

import { stripHtmlTags } from "@/lib/utils/sanitization";
import { formatDateShort } from "@/lib/utils/formatting";
import FetchModal from "@/fetch-ui/FetchModal";
import { FetchPill } from "@/fetch-ui/FetchPill";
import { getReadableTextColor } from "@/features/landing-pages/_logic/color-utils";
import type { ScheduleItemRow, ScheduleLabelRow } from "../../../../_logic/types/db-types";
import { formatScheduleDateTime, getItemLabels } from "../../../../_logic/utils/schedule-utils";
import { useBuilderContext } from "../../../../_hooks/useBuilderContext";
import { useLinkedActivity } from "../../../../_hooks/useLinkedActivity";
import { useActivityRuleState } from "../../../../_hooks/useActivityRuleState";
import { useRegisteredActivityIds } from "../../../../_hooks/useRegisteredActivityIds";
import { useSignupLimitRules } from "../../../../_hooks/useSignupLimitRules";
import { isActivitySignupClosed } from "../../../../_logic/utils/activity-signup-status";
import { ActivitySignupModal } from "../../../public/ActivitySignupModal";
import { usePublicViewContext } from "../../../public/PublicViewContext";
import { SanitizedHtml } from "../_shared/SanitizedHtml";

interface ScheduleDetailModalProps {
  /** The schedule item to display */
  item: ScheduleItemRow;
  /** Available labels for label ID resolution */
  labels: ScheduleLabelRow[];
  /** Primary color for time badge */
  primaryColor: string;
  /** Whether to show the item's time-of-day alongside its date. Defaults to true. */
  showTimeLabels?: boolean;
  /** Close handler */
  onClose: () => void;
}

/** Renders a modal with full details for a single schedule item */
export function ScheduleDetailModal({
  item,
  labels,
  primaryColor,
  showTimeLabels = true,
  onClose,
}: ScheduleDetailModalProps) {
  // Hooks
  const { eventId, timeZone } = useBuilderContext();
  const { isPublic, landingPageId, signedUpSlotIds, waitlistedSlotIds, bumpRegistrations } = usePublicViewContext();
  const hasLinkedActivity = isPublic && item.activity_id != null;
  const { activity, slots, slotCounts, isLoading, hasError, refresh } = useLinkedActivity(
    item.activity_id,
    hasLinkedActivity
  );

  // Event-wide data for the day-cap/exclusion-group hint (evaluateRuleBlock needs the viewer's
  // registrations across the WHOLE event, not just this one linked activity, since a day cap
  // counts every activity's registrations on a date and an exclusion group can span multiple
  // activities). Only fetched once a linked activity is shown — same pipeline ActivitiesRenderer
  // builds from its own page-load fetch, shared here so the two can't drift.
  const { rules: signupLimitRules, timeSlotsByActivity: eventTimeSlotsByActivity } = useSignupLimitRules(
    eventId,
    hasLinkedActivity
  );

  // Derived values — same status-filtered (registered, not waitlisted) shape ActivitiesRenderer
  // builds, over the event-wide slot map so it also captures registrations on OTHER activities.
  const { registeredActivityIds, registeredCountByDate } = useRegisteredActivityIds(
    eventTimeSlotsByActivity,
    signedUpSlotIds,
    waitlistedSlotIds
  );

  // Derived values — client-side day-cap/exclusion-group hint for this one activity's slots.
  const ruleState = useActivityRuleState({
    activities: activity ? [activity] : [],
    timeSlotsByActivity: eventTimeSlotsByActivity,
    registeredActivityIds,
    registeredCountByDate,
    rules: signupLimitRules,
  });

  // Stable identity for ActivitySignupModal's `ruleBlock` prop — an inline object literal here
  // would change identity every render and defeat the memoization inside that modal's own
  // slotStates useMemo.
  const activitySignupRuleBlock = useMemo(() => ({ bySlotId: ruleState.bySlotId }), [ruleState.bySlotId]);

  // Refresh this activity's slot counts AND signal the page so the schedule's own "Registered"
  // pills re-sync — the signup happened here, not through the Activities section's reconcile.
  const handleSignupSuccess = useCallback(() => {
    refresh();
    bumpRegistrations();
  }, [refresh, bumpRegistrations]);

  // Derived values — when times are hidden, fall back to a date-only string (or null for
  // undated items, matching CalendarEventCard's "nothing to show" behavior).
  const dateTimeDisplay = useMemo(() => {
    if (showTimeLabels) return formatScheduleDateTime(item.date, item.start_time, item.end_time, timeZone);
    return item.date ? formatDateShort(item.date) : null;
  }, [item.date, item.start_time, item.end_time, timeZone, showTimeLabels]);
  const itemLabels = useMemo(() => getItemLabels(item.label_ids, labels), [labels, item.label_ids]);
  const bookableSlots = useMemo(() => slots.filter((s) => s.is_bookable), [slots]);

  // Rich-text editors leave behind empty markup ("<p></p>", "<p>&nbsp;</p>") that is
  // truthy but renders as a blank prose block — a phantom gap in the modal. Treat the
  // description as present only when it has real text or structural content.
  const hasDescription = useMemo(() => {
    const raw = item.description;
    if (!raw) return false;
    if (/<(table|ul|ol)\b/i.test(raw)) return true;
    return (
      stripHtmlTags(raw)
        .replace(/&nbsp;|&#160;/gi, " ")
        .trim().length > 0
    );
  }, [item.description]);

  // Modal portals outside SectionContainer's CSS-var scope, so set --lp-accent locally.
  const accentStyle = useMemo(
    () => ({ "--lp-accent": getReadableTextColor(primaryColor) }) as React.CSSProperties,
    [primaryColor]
  );

  // Render — when the schedule item has a linked activity in the public view, ALWAYS route to the
  // signup flow; never show the description/detail modal. Once the activity loads we render the
  // signup modal; while it loads (or if it can't be resolved — deleted, invite-gated) we keep the
  // viewer in the signup context with a loading / unavailable state instead of the detail view.

  // Signups closed — mirror the Activities card + server gate. Show a closed notice instead of the
  // signup form so the schedule can't offer a signup the server would reject.
  if (hasLinkedActivity && activity && isActivitySignupClosed(activity)) {
    return (
      <FetchModal
        isOpen
        onClose={onClose}
        title={activity.title}
        size="auto"
        className="m-0 h-dvh max-h-dvh w-screen rounded-none p-4 sm:my-8 sm:h-auto sm:max-h-[90vh] sm:w-full sm:max-w-lg sm:rounded-3xl sm:p-6"
        contentClassName="overflow-y-auto"
      >
        <p className="text-sm font-medium text-gray-500">Signups are closed for this activity.</p>
      </FetchModal>
    );
  }

  if (hasLinkedActivity && activity) {
    return (
      <ActivitySignupModal
        activity={activity}
        timeSlots={bookableSlots}
        slotCounts={slotCounts}
        primaryColor={primaryColor}
        landingPageId={landingPageId}
        ruleBlock={activitySignupRuleBlock}
        onClose={onClose}
        onSuccess={handleSignupSuccess}
      />
    );
  }

  if (hasLinkedActivity) {
    return (
      <FetchModal
        isOpen
        onClose={onClose}
        title={item.title}
        size="auto"
        className="m-0 h-dvh max-h-dvh w-screen rounded-none p-4 sm:my-8 sm:h-auto sm:max-h-[90vh] sm:w-full sm:max-w-lg sm:rounded-3xl sm:p-6"
        contentClassName="overflow-y-auto"
      >
        {isLoading ? (
          <p className="text-sm font-medium text-gray-400" aria-live="polite" aria-busy="true">
            Loading sign-up details…
          </p>
        ) : (
          <p role="alert" className="text-sm font-medium text-red-600">
            Sign-up details couldn&apos;t load. Try again later.
          </p>
        )}
      </FetchModal>
    );
  }

  return (
    <FetchModal
      isOpen
      onClose={onClose}
      title={item.title}
      size="auto"
      // Mobile: tall (90dvh) bottom sheet, flush to the screen edge with rounded top corners,
      // that slides up and can be swiped down to dismiss. Desktop (sm+): centered max-w-lg card.
      swipeToDismiss
      className="m-0 h-[90dvh] max-h-[90dvh] w-full rounded-b-none sm:my-8 sm:h-auto sm:max-h-[90vh] sm:w-full sm:max-w-lg sm:rounded-b-3xl"
      contentClassName="overflow-y-auto"
    >
      <div className="flex flex-col gap-4" style={accentStyle}>
        {itemLabels.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {itemLabels.map((label) => (
              <FetchPill key={label.id} as="span" size="sm" color={label.color || undefined} selected>
                {label.name}
              </FetchPill>
            ))}
          </div>
        )}

        <div className="flex flex-col gap-2.5">
          {/* Each row pins its icon to the first line (mt-0.5 centers a 16px icon on a 20px line),
              so a value that wraps, like a long speaker list, keeps the icon beside its start. */}
          {dateTimeDisplay && (
            <div className="flex items-start gap-2.5 text-sm font-semibold text-gray-900">
              <Clock className="mt-0.5 h-4 w-4 shrink-0" style={{ color: primaryColor }} aria-hidden="true" />
              <span className="min-w-0 wrap-break-word">{dateTimeDisplay}</span>
            </div>
          )}

          {item.location && (
            <div className="flex items-start gap-2.5 text-sm font-medium text-gray-700">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
              <span className="min-w-0 wrap-break-word">{item.location}</span>
            </div>
          )}

          {item.speaker && (
            <div className="flex items-start gap-2.5 text-sm font-medium text-gray-700">
              <User className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
              <span className="min-w-0 wrap-break-word">{item.speaker}</span>
            </div>
          )}

          {item.vendor && (
            <div className="flex items-start gap-2.5 text-sm font-medium text-gray-700">
              <Award className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
              <span className="min-w-0 wrap-break-word">{item.vendor}</span>
            </div>
          )}
        </div>

        {/* Description */}
        {hasDescription && item.description && (
          <SanitizedHtml
            html={item.description}
            className="prose prose-sm max-w-none text-gray-700 prose-a:text-(--lp-accent) prose-a:underline"
          />
        )}
      </div>
    </FetchModal>
  );
}
